import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MapView from '../components/MapView';
import HistoricalCompare from '../components/map/HistoricalCompare';
import { RecipientsPanel } from '../components/map/RecipientsPanel';
import { ArrowLeft, ClockCounterClockwise, Lock, Users } from '@phosphor-icons/react';
import { generateMeasurementPDF } from '../utils/pdfExport';
import { getCapabilities } from '../lib/planFeatures';

export default function ProjectPage() {
  const { projectId } = useParams();
  const { apiCall, user } = useAuth();
  const navigate = useNavigate();
  const capabilities = getCapabilities(user);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapInstance, setMapInstance] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);

  const handleMapInstance = useCallback((m) => { setMapInstance(m); }, []);

  useEffect(() => {
    if (projectId) loadProject();
  }, [projectId]);

  async function loadProject() {
    try {
      const res = await apiCall('get', `/projects/${projectId}`);
      setProject(res.data);
    } catch (e) {
      console.error('Failed to load project:', e);
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveMeasurement(measurement) {
    if (!projectId) return measurement;
    try {
      const res = await apiCall('post', '/measurements', { project_id: projectId, ...measurement });
      return res.data;
    } catch (e) { console.error('Failed to save measurement:', e); return measurement; }
  }

  async function handleDeleteMeasurement(measurement) {
    if (!projectId || !measurement?.id) return;
    try {
      await apiCall('delete', `/measurements/${projectId}/${measurement.id}`);
    } catch (e) { console.error('Failed to delete measurement:', e); }
  }

  async function handleUpdateMeasurement(measurementId, updates) {
    if (!projectId || !measurementId) return;
    try {
      await apiCall('patch', `/measurements/${projectId}/${measurementId}`, updates);
    } catch (e) { console.error('Failed to update measurement:', e); }
  }

  async function handleSaveLocation(location) {
    if (!projectId) return;
    try {
      await apiCall('put', `/projects/${projectId}`, { location });
    } catch (e) { console.error('Failed to save location:', e); }
  }

  async function handleExportPDF() {
    try {
      const res = await apiCall('get', `/projects/${projectId}/export`);
      generateMeasurementPDF(res.data.project, res.data.measurements);
    } catch (e) { console.error('Export failed:', e); }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Project not found</p>
      </div>
    );
  }

  return (
    <div className="h-full relative" data-testid="project-page">
      <button
        onClick={() => navigate('/projects')}
        className="absolute top-1 left-2 z-20 flex items-center gap-2 bg-card/90 backdrop-blur-sm border border-border shadow-card rounded-md px-2.5 py-1.5 text-xs font-medium hover:bg-card transition-colors"
        data-testid="back-to-projects"
      >
        <ArrowLeft size={14} weight="bold" />
        <span className="hidden sm:inline">{project.name}</span>
      </button>

      {/* Time Travel — Enterprise-only historical imagery compare */}
      {!compareOpen && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
          {capabilities.timeTravel && (
            <button
              onClick={() => setRecipientsOpen(true)}
              className="flex items-center gap-2 bg-card/90 backdrop-blur-sm border border-border shadow-card rounded-md px-3 py-2 text-sm font-medium hover:bg-card transition-colors"
              title="See who's been emailed a time-lapse link for this project"
              data-testid="recipients-btn"
            >
              <Users size={16} weight="bold" />
              <span className="hidden sm:inline">Recipients</span>
            </button>
          )}
          <button
            onClick={() => {
              if (!capabilities.timeTravel) {
                navigate('/my-billing');
                return;
              }
              setCompareOpen(true);
            }}
            className={`flex items-center gap-2 backdrop-blur-sm border shadow-card rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              capabilities.timeTravel
                ? 'bg-card/90 border-border hover:bg-card text-foreground'
                : 'bg-card/70 border-border/60 text-muted-foreground hover:bg-card/90'
            }`}
            title={capabilities.timeTravel
              ? 'Compare two dates of satellite imagery'
              : 'Time Travel is an Enterprise feature — click to upgrade'}
            data-testid="time-travel-btn"
          >
            {capabilities.timeTravel ? (
              <ClockCounterClockwise size={16} weight="bold" />
            ) : (
              <Lock size={14} weight="bold" />
            )}
            <span className="hidden sm:inline">Time Travel</span>
          </button>
        </div>
      )}

      <MapView
        project={project}
        onSaveMeasurement={handleSaveMeasurement}
        onDeleteMeasurement={handleDeleteMeasurement}
        onUpdateMeasurement={handleUpdateMeasurement}
        onExportPDF={handleExportPDF}
        onSaveLocation={handleSaveLocation}
        onMapInstance={handleMapInstance}
      />

      {compareOpen && capabilities.timeTravel && (
        <HistoricalCompare
          underlyingMap={mapInstance}
          apiCall={apiCall}
          projectId={projectId}
          projectName={project?.name}
          projectAddress={project?.map_data?.address}
          onClose={() => setCompareOpen(false)}
        />
      )}

      {recipientsOpen && capabilities.timeTravel && (
        <RecipientsPanel
          projectId={projectId}
          projectName={project?.name}
          apiCall={apiCall}
          onClose={() => setRecipientsOpen(false)}
        />
      )}
    </div>
  );
}
